import { useEffect, useRef, useState } from 'react';

import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle, IconSend, IconX } from '@tabler/icons-react';

import { useReportFeedback } from '@/hooks/useReportFeedback';
import { useStickToBottom } from '@/hooks/useStickToBottom';
import { requestAnchorHighlight } from '@/lib/anchorHighlight';
import type { FeedbackAnchors } from '@/types/report-feedback';
import classes from './ReportFeedbackChat.module.css';

interface ReportFeedbackChatPanelProps {
  reportId: string;
  context: string | null;
  /** paraId anchors captured with the selection (docx Content view). */
  anchors?: FeedbackAnchors | null;
  onClearContext: () => void;
  onFeedbackSubmitted?: () => void;
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ReportFeedbackChatPanel({
  reportId,
  context,
  anchors = null,
  onClearContext,
  onFeedbackSubmitted,
}: Readonly<ReportFeedbackChatPanelProps>) {
  const [inputValue, setInputValue] = useState('');
  const messagesRef = useRef<HTMLDivElement>(null);

  const { feedbackHistory, isLoading, isSubmitting, error, submitFeedback, fetchFeedback } =
    useReportFeedback();

  useEffect(() => {
    if (reportId) {
      fetchFeedback(reportId);
    }
  }, [reportId, fetchFeedback]);

  useStickToBottom(messagesRef, feedbackHistory.length, reportId);

  const handleSubmit = async () => {
    const feedback = inputValue.trim();
    if (!feedback || !reportId) {
      return;
    }

    setInputValue('');
    await submitFeedback(reportId, feedback, context ?? undefined, anchors);
    onClearContext();
    onFeedbackSubmitted?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={classes.panelBody}>
      <div ref={messagesRef} className={classes.messages}>
        {isLoading ? (
          <Stack align="center" justify="center" style={{ flex: 1 }}>
            <Loader size="sm" />
          </Stack>
        ) : feedbackHistory.length === 0 ? (
          <div className={classes.emptyState}>
            <Text size="sm">No feedback yet.</Text>
            <Text size="xs" mt="xs">
              Select text in the report for targeted feedback, or type a message below.
            </Text>
          </div>
        ) : (
          feedbackHistory.map((item) => (
            <div key={item.id} className={classes.message}>
              {item.context && <div className={classes.contextQuote}>"{item.context}"</div>}
              <Text size="sm">{item.feedback}</Text>
              <Text size="xs" c="dimmed" mt={4}>
                {formatDate(item.created_at)}
              </Text>
            </div>
          ))
        )}
      </div>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" mb="xs">
          {error}
        </Alert>
      )}

      <div className={classes.inputArea}>
        {context && (
          <div className={classes.contextBadge}>
            {anchors && anchors.para_ids.length > 0 ? (
              <UnstyledButton
                style={{ flex: 1 }}
                onClick={() => requestAnchorHighlight(anchors.para_ids)}
                title="Show this selection in the report"
                data-testid="compose-context"
              >
                <Text size="xs" lineClamp={3}>
                  Context: "{context}"
                  <Text span size="xs" c="dimmed">
                    {' '}
                    · anchored to {anchors.para_ids.length} block
                    {anchors.para_ids.length === 1 ? '' : 's'} — click to show
                  </Text>
                </Text>
              </UnstyledButton>
            ) : (
              <Text size="xs" lineClamp={3} style={{ flex: 1 }}>
                Context: "{context}"
              </Text>
            )}
            <ActionIcon size="xs" variant="subtle" onClick={onClearContext}>
              <IconX size={12} />
            </ActionIcon>
          </div>
        )}
        <Group gap="xs" align="flex-end">
          <Textarea
            placeholder="Type your feedback..."
            value={inputValue}
            onChange={(e) => setInputValue(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            minRows={1}
            maxRows={4}
            autosize
            style={{ flex: 1 }}
            disabled={isSubmitting}
          />
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!inputValue.trim()}
            size="sm"
            variant="filled"
          >
            <IconSend size={16} />
          </Button>
        </Group>
      </div>
    </div>
  );
}
